import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, filterParams, filterKey } from "@/lib/api";
import { fmtInt, fmtCompact, fmtCost } from "@/lib/format";
import { Coins, CalendarDays, DollarSign, Activity } from "lucide-react";

function Card({ testid, overline, value, sub, icon: Icon, accent }) {
  return (
    <div className="relative bg-card border border-border p-5 group overflow-hidden" data-testid={testid}>
      <div className="absolute top-0 left-0 h-full w-[3px]" style={{ background: accent }} />
      <div className="flex items-start justify-between">
        <p className="overline">{overline}</p>
        <Icon className="w-4 h-4 text-muted-foreground group-hover:text-white transition-colors" />
      </div>
      <p className="font-mono text-3xl font-semibold mt-3 tracking-tight tabular-nums">{value}</p>
      {sub && <p className="font-mono text-xs text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
}

export default function SummaryCards({ filters }) {
  const { data } = useQuery({
    queryKey: ["summary", filters.user, filters.model, filters.session, filters.token],
    queryFn: async () =>
      (await api.get("/summary", { params: filterParams(filters, { includeRange: false }) })).data,
    refetchInterval: 20000,
  });

  const t = data?.tokens_today || {};
  const w = data?.tokens_week || {};
  const m = data?.tokens_month || {};

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-px bg-border border border-border">
      <Card testid="card-tokens-today" overline="Tokens today" accent="#3B82F6" icon={Coins}
        value={fmtCompact(t.total)} sub={`${fmtInt(t.input)} in · ${fmtInt(t.output)} out`} />
      <Card testid="card-tokens-week" overline="Tokens this week" accent="#3B82F6" icon={CalendarDays}
        value={fmtCompact(w.total)} sub={`${fmtInt(w.cache)} cache tokens`} />
      <Card testid="card-tokens-month" overline="Tokens this month" accent="#3B82F6" icon={CalendarDays}
        value={fmtCompact(m.total)} sub={`${fmtInt(m.total)} total`} />
      <Card testid="card-cost-today" overline="Cost today" accent="#F59E0B" icon={DollarSign}
        value={fmtCost(data?.cost_today)} sub={`${fmtCost(data?.cost_month)} this month`} />
      <Card testid="card-active-sessions" overline="Active sessions" accent="#22C55E" icon={Activity}
        value={fmtInt(data?.active_sessions)} sub={`${fmtInt(data?.total_sessions)} total · last 30m`} />
    </div>
  );
}
